/* eslint-disable @typescript-eslint/no-invalid-void-type -- required to allow no input params on a query */
import type { BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { createApi, retry } from '@reduxjs/toolkit/query/react'
import { toast } from 'sonner'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import isEqual from 'lodash/isEqual'
import { endOfDay, startOfDay } from 'date-fns'
import {
  type ServiceCursorPaginatedResponseType,
  ServiceErrorHandler,
} from '@/connect-types/backend/service'
import { customAxios } from '@/utils/axiosHelper'
import { buildUrl } from '@/utils/common'
import {
  type ReviewSentimentType,
  type UserReviewPlatforms,
} from '@/connect-types/reviews/reviews.type'
import config from '@/config'
import { getToken } from '@/utils/AuthTokenHelper'
import type {
  ReviewResponseType,
  ReviewPageType,
  ReviewPageListItemType,
  SimpleReviewReportType,
  ReviewReportType,
} from '../morpheus/types/reviews'
import { shouldBailout } from '../helpers'
import type {
  CustomPromptType,
  CustomCopyType,
  GooglePlace,
  AspectAnalysisResult,
  IntentionAnalysisResult,
  HighlightResult,
  PlacesApiReview,
  TrendData,
  CompetitorComparisonResult,
  LocalStandingResult,
  TrackedCompetitorsSummaryResult,
  GoogleAccount,
  GoogleLocation,
  GoogleConnectionResponse,
  CustomLinkType,
  AutoGenerationRuleType,
  PendingReplyType,
  PendingRepliesListResponse,
  BatchAcceptResponse,
  PendingReplyActivityResponse,
  ActivityLogQueryParams,
  ActivityLogResponse,
  ReviewMetricsAccumulationType,
  NotificationEmailType,
  NotificationEmailsListResponse,
  NotificationEmailsSearchResponse,
  ResendEmailResponse,
  PreviewEmailResponse,
  MetricsTimeseriesDataPoint,
  AiAnalyticsQueueStatusResponse,
  AiAnalyticsResponseTimesResponse,
  AiAnalyticsActivityFeedResponse,
  AiAnalyticsComparisonResponse,
  AiAnalyticsByRatingResponse,
  AiAnalyticsTrendsResponse,
  VoiceOfMerchantResponse,
  BulkVoiceOfMerchantItem,
  ResponseRateResponse,
  ResponseRateQueryParams,
} from './tank.types'

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<{
    url: string
    method: AxiosRequestConfig['method']
    data?: AxiosRequestConfig['data']
    params?: AxiosRequestConfig['params']
    headers?: AxiosRequestConfig['headers']
  }> =>
  async ({ url, method, data, params, headers }) => {
    try {
      let axiosUrl = baseUrl + url
      if (url.startsWith('https://')) {
        axiosUrl = url
      }

      const result = await customAxios({
        url: axiosUrl,
        method,
        data,
        params,
        headers,
      })

      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError

      if (
        method !== 'get' &&
        err.response?.status !== 401 &&
        err.response?.status !== 404 &&
        err.response?.status !== 429 &&
        !url.includes('segment')
      ) {
        ServiceErrorHandler(err)
      }

      if (shouldBailout(err)) {
        retry.fail(err)
      }

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      }
    }
  }

const tankApi = createApi({
  reducerPath: 'tank',
  baseQuery: retry(axiosBaseQuery({ baseUrl: config.url.tank }), {
    maxRetries: 2,
  }),
  tagTypes: [
    'ReviewPage',
    'ReviewResponses',
    'GoogleConnection',
    'GoogleAccount',
    'PlaceReviews',
    'Prompts',
    'CustomCopy',
    'TrackedCompetitors',
    'CustomLinks',
    'GenerationRules',
    'PendingReplies',
    'ConfirmationExemptions',
    'NotificationEmails',
  ],
  endpoints: (build) => ({
    getOauthToken: build.query<
      {
        success: boolean
        error?: string
        details?: unknown
        reauthorized?: boolean
        newAccount?: boolean
        duplicate?: boolean
      },
      { code: string; oauth_uid: string; state: string }
    >({
      query: ({ code, oauth_uid, state }) => ({
        method: 'GET',
        url: `/oauth/token`,
        params: { code, oauth_uid, state },
      }),
    }),
    deleteOauthToken: build.mutation<
      { success: boolean; error?: string; details?: unknown },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'DELETE',
        url: `/oauth/token/${orgId}`,
      }),
    }),
    getGoogleConnection: build.query<
      GoogleConnectionResponse,
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/${orgId}/google/connection`,
        method: 'GET',
      }),
      providesTags: (_result, _error, params) => [
        { type: 'GoogleConnection', id: params.orgId },
      ],
    }),
    getGoogleAccount: build.query<
      { accounts: GoogleAccount[] },
      { orgId: string; sync?: boolean }
    >({
      query: ({ orgId, sync }) => ({
        url: `/${orgId}/google/accounts`,
        method: 'GET',
        params: sync ? { sync: true } : undefined,
      }),
      providesTags: (_result, _error, params) => [
        { type: 'GoogleAccount', id: params.orgId },
      ],
    }),
    verifyGoogleAccount: build.mutation<
      unknown,
      { orgId: string; account_id: string }
    >({
      query: ({ orgId, account_id }) => ({
        url: `/${orgId}/google/accounts/${account_id}/verify`,
        method: 'PUT',
      }),
      invalidatesTags: (_result, _error, params) => [
        { type: 'GoogleAccount', id: params.orgId },
      ],
    }),
    deleteGoogleAccount: build.mutation<
      unknown,
      { orgId: string; account_id: string }
    >({
      query: ({ orgId, account_id }) => ({
        url: `/${orgId}/google/accounts/${account_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, params) => [
        { type: 'GoogleAccount', id: params.orgId },
      ],
    }),
    getGoogleLocations: build.query<
      { locations: GoogleLocation[] },
      { orgId: string; account_id: string }
    >({
      query: ({ orgId, account_id }) => ({
        url: `/${orgId}/google/accounts/${account_id}/locations`,
        method: 'GET',
      }),
      providesTags: (_result, _error, params) => [
        { type: 'GoogleAccount', id: params.orgId },
      ],
    }),
    getGoogleLocationsForMultipleAccounts: build.mutation<
      { locations: GoogleLocation[] },
      { orgId: string; account_ids: string[]; sync?: boolean }
    >({
      query: ({ orgId, account_ids, sync }) => ({
        url: `/${orgId}/google/accounts/locations`,
        method: 'POST',
        data: { account_ids },
        params: sync ? { sync } : undefined,
      }),
      invalidatesTags: (_result, _error, params) => [
        { type: 'GoogleAccount', id: params.orgId },
      ],
    }),
    getGoogleLocation: build.query<
      GoogleLocation,
      { orgId: string; account_id: string; location_id: string }
    >({
      query: ({ orgId, account_id, location_id }) => ({
        url: `/${orgId}/google/accounts/${account_id}/locations/${location_id}`,
        method: 'GET',
      }),
    }),

    getGoogleLocationVoiceOfMerchant: build.query<
      VoiceOfMerchantResponse,
      { orgId: string; account_id: string; location_id: string }
    >({
      query: ({ orgId, account_id, location_id }) => ({
        url: `/${orgId}/google/accounts/${account_id}/locations/${location_id}/voice-of-merchant`,
        method: 'GET',
      }),
    }),

    getGoogleLocationVerificationOptions: build.query<
      unknown,
      { orgId: string; account_id: string; location_id: string }
    >({
      query: ({ orgId, account_id, location_id }) => ({
        url: `/${orgId}/google/accounts/${account_id}/locations/${location_id}/verification-options`,
        method: 'GET',
      }),
    }),

    getBulkVoiceOfMerchant: build.query<
      BulkVoiceOfMerchantItem[],
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/${orgId}/google/locations/voice-of-merchant`,
        method: 'GET',
      }),
    }),

    deleteGoogleLocation: build.mutation<
      GoogleLocation,
      { orgId: string; account_id: string; location_id: string }
    >({
      query: ({ orgId, account_id, location_id }) => ({
        url: `/${orgId}/google/accounts/${account_id}/locations/${location_id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, params) => [
        { type: 'GoogleAccount', id: params.orgId },
      ],
    }),
    batchLinkGoogleLocations: build.mutation<
      { success: boolean },
      {
        orgId: string
        account_id: string
        locations: { location_id: string; review_settings_id: string }[]
      }
    >({
      query: ({ orgId, account_id, locations }) => ({
        url: `/${orgId}/google/accounts/${account_id}/locations/batch-link`,
        method: 'POST',
        data: { data: locations },
      }),
      invalidatesTags: (_result, _error, params) => [
        { type: 'GoogleAccount', id: params.orgId },
      ],
    }),
    putGoogleReviewReply: build.mutation<
      unknown,
      {
        orgId: string
        location_id: string
        account_id: string
        review_id: string
        tank_review_id: string
        reply: string
      }
    >({
      query: ({
        orgId,
        location_id,
        account_id,
        review_id,
        tank_review_id,
        reply,
      }) => ({
        url: `/${orgId}/google/accounts/${account_id}/locations/${location_id}/reviews/${review_id}/reply`,
        method: 'PUT',
        data: { comment: reply },
        params: { id: tank_review_id },
      }),
      invalidatesTags: (_result, _error, params) => [
        { type: 'ReviewResponses', id: params.tank_review_id },
      ],
    }),
    delGoogleReviewReply: build.mutation<
      ReviewResponseType,
      {
        orgId: string
        location_id: string
        account_id: string
        review_id: string
        tank_review_id: string
      }
    >({
      query: ({
        orgId,
        location_id,
        account_id,
        review_id,
        tank_review_id,
      }) => ({
        url: `/${orgId}/google/accounts/${account_id}/locations/${location_id}/reviews/${review_id}/reply`,
        method: 'DELETE',
        params: { id: tank_review_id },
      }),
      invalidatesTags: (_result, _error, params) => [
        { type: 'ReviewResponses', id: params.tank_review_id },
      ],
    }),

    generateGoogleReviewReply: build.mutation<
      { completion: string },
      {
        orgId: string
        review_id: string
        review_text: string
        review_rating: string
        reviewer_display_name?: string
        review_settings_id?: string
        google_location_name?: string
      }
    >({
      query: ({
        orgId,
        review_id,
        review_text,
        review_rating,
        reviewer_display_name,
        review_settings_id,
        google_location_name,
      }) => ({
        url: `/${orgId}/reviews/${review_id}/reviews/generate-response`,
        method: 'GET',
        params: {
          review_text,
          review_rating,
          reviewer_display_name,
          review_settings_id,
          google_location_name,
        },
      }),
    }),

    streamGoogleReviewReply: build.mutation<
      { completion: string },
      {
        orgId: string
        review_id: string
        review_text: string
        review_rating: string
        reviewer_display_name?: string
        review_settings_id?: string
        google_location_name?: string
        custom_prompt_id?: string
        test_prompt?: string
        onChunk?: (chunk: string) => void
      }
    >({
      queryFn: async (args, _api, _extraOptions, baseQuery) => {
        const { onChunk, ...queryArgs } = args

        try {
          const body = {
            review_text: queryArgs.review_text ?? '',
            ...(queryArgs.review_rating && {
              review_rating: queryArgs.review_rating,
            }),
            ...(queryArgs.reviewer_display_name && {
              reviewer_display_name: queryArgs.reviewer_display_name,
            }),
            ...(queryArgs.review_settings_id && {
              review_settings_id: queryArgs.review_settings_id,
            }),
            ...(queryArgs.google_location_name && {
              google_location_name: queryArgs.google_location_name,
            }),
            ...(queryArgs.custom_prompt_id && {
              custom_prompt_id: queryArgs.custom_prompt_id,
            }),
            ...(queryArgs.test_prompt && {
              test_prompt: queryArgs.test_prompt,
            }),
          }

          const url = `/${queryArgs.orgId}/reviews/${queryArgs.review_id}/reviews/stream-response`

          const token = getToken()?.accessToken

          const response = await fetch(`${config.url.tank}${url}`, {
            method: 'POST',
            headers: {
              Accept: 'text/event-stream',
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
          })

          if (!response.ok) {
            throw new Error(`Error ${response.status}: ${response.statusText}`)
          }

          if (!response.body) {
            throw new Error('ReadableStream not supported')
          }

          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let fullText = ''

          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            const chunk = decoder.decode(value, { stream: true })
            const lines = chunk.split('\n\n')

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const jsonStr = line.substring(6)
                  const jsonData = JSON.parse(jsonStr) as string
                  fullText += jsonData || ''

                  if (onChunk && jsonData) {
                    onChunk(jsonData)
                  }
                } catch (e) {
                  console.error('Error parsing SSE data:', e)
                }
              }
            }
          }

          return { data: { completion: fullText } }
        } catch (error) {
          return {
            error: {
              status: 'ERROR',
              data: error instanceof Error ? error.message : 'Unknown error',
            },
          }
        }
      },
    }),
    getGoogleReviews: build.query<
      { reviews: unknown[] },
      { orgId: string; account_id: string; location_id: string }
    >({
      query: ({ orgId, account_id, location_id }) => ({
        url: `/${orgId}/google/accounts/${account_id}/locations/${location_id}/reviews`,
        method: 'GET',
      }),
    }),
    getGoogleReview: build.query<
      { review: unknown },
      {
        orgId: string
        account_id: string
        location_id: string
        review_id: string
      }
    >({
      query: ({ orgId, account_id, location_id, review_id }) => ({
        url: `/${orgId}/google/accounts/${account_id}/locations/${location_id}/reviews/${review_id}`,
        method: 'GET',
      }),
    }),

    getPlacesNearbySearch: build.query<
      { places: GooglePlace[] },
      {
        orgId: string
        latitude: number
        longitude: number
        types: string
        excludedTypes?: string
        queryPlaceIds?: string[]
        maxResults?: number
        limitAnalysis?: number
        radius?: number
      }
    >({
      query: ({
        orgId,
        latitude,
        longitude,
        types,
        queryPlaceIds,
        excludedTypes,
        maxResults,
        limitAnalysis,
        radius,
      }) => ({
        method: 'GET',
        url: `/${orgId}/google/places/nearby-search`,
        params: {
          latitude,
          longitude,
          types,
          ...(excludedTypes && { excludedTypes }),
          ...(maxResults && { maxResults }),
          ...(queryPlaceIds && { queryPlaceIds }),
          ...(limitAnalysis && { limitAnalysis }),
          ...(radius && { radius }),
        },
      }),
    }),

    getPlacesTextSearch: build.query<
      { places: GooglePlace[] },
      {
        orgId: string
        latitude: number
        longitude: number
        textQuery: string
        queryPlaceIds?: string[]
      }
    >({
      query: ({ orgId, latitude, longitude, textQuery, queryPlaceIds }) => ({
        method: 'GET',
        url: `/${orgId}/google/places/text-search`,
        params: {
          latitude,
          longitude,
          textQuery,
          ...(queryPlaceIds && { queryPlaceIds }),
        },
      }),
    }),

    getPlaceReviews: build.query({
      query: ({ orgId, place_id }) => ({
        url: `/${orgId}/google/places/${place_id}/reviews`,
        method: 'GET',
      }),
      transformResponse: (response: any) => response,
      providesTags: (_result, _error, { place_id }) => [
        { type: 'PlaceReviews', id: place_id },
      ],
    }),

    getPreliminaryCompetitorIntentions: build.query<
      { competitor_name: string; intentions: IntentionAnalysisResult },
      {
        orgId: string
        competitor_name: string
        data: {
          reviews: PlacesApiReview[]
        }
      }
    >({
      query: ({ orgId, competitor_name, data }) => ({
        method: 'POST',
        url: `/${orgId}/analysis/preliminary-competitor-intentions?competitor_name=${encodeURIComponent(competitor_name)}`,
        data,
      }),
    }),

    getPreliminaryCompetitorAspects: build.query<
      { competitor_name: string; aspects: AspectAnalysisResult },
      {
        orgId: string
        competitor_name: string
        data: {
          reviews: PlacesApiReview[]
        }
      }
    >({
      query: ({ orgId, competitor_name, data }) => ({
        method: 'POST',
        url: `/${orgId}/analysis/preliminary-competitor-aspects?competitor_name=${encodeURIComponent(competitor_name)}`,
        data,
      }),
    }),

    getPreliminaryCompetitorHighlights: build.query<
      {
        competitor_name: string
        google_place_id: string
        highlights: HighlightResult[]
        review_count: number
      },
      {
        orgId: string
        competitor_name: string
        google_place_id: string
        data: {
          reviews: PlacesApiReview[]
        }
      }
    >({
      query: ({ orgId, competitor_name, google_place_id, data }) => ({
        method: 'POST',
        url: `/${orgId}/analysis/preliminary-competitor-highlights?competitor_name=${encodeURIComponent(competitor_name)}&google_place_id=${google_place_id}`,
        data,
      }),
    }),

    getPreliminaryCompetitorVelocity: build.query<
      {
        velocity: {
          reviewsPerMonth: number
          daysBetweenReviews: number
          totalReviewsAnalyzed: number
        }
        competitor_name: string
      },
      {
        orgId: string
        place_id: string
        competitor_name: string
      }
    >({
      query: ({ orgId, place_id, competitor_name }) => ({
        method: 'GET',
        url: `/${orgId}/analysis/preliminary-competitor-velocity?place_id=${place_id}&competitor_name=${encodeURIComponent(competitor_name)}`,
      }),
    }),

    getPreliminaryCompetitorSummary: build.query<
      unknown,
      {
        orgId: string
        data: {
          comparisonData: any
          venueName: string
          competitorName: string
          review_settings_id: string
        }
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/${orgId}/analysis/preliminary-competitor-summary?key=${encodeURIComponent(data.review_settings_id + data.competitorName)}`,
        data,
      }),
    }),

    getPreliminaryComparativeData: build.query<
      GooglePlace & {
        trend: TrendData
        intentions: IntentionAnalysisResult
        aspects: AspectAnalysisResult
      },
      {
        orgId: string
        serial?: string
        page_id?: string
        bypass_cache?: boolean
      }
    >({
      query: ({ orgId, serial, page_id, bypass_cache }) => ({
        method: 'GET',
        url: `/${orgId}/analysis/preliminary-comparative-data`,
        params: {
          ...(serial && serial !== 'unlinked' && { serial }),
          ...(page_id && { page_id }),
          ...(bypass_cache && { bypass_cache }),
        },
        headers: {
          ...(bypass_cache && {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
          }),
        },
      }),
    }),

    /**
     * All the venue-vs-competitor "maths" for the competitor analysis view -
     * similarity score, rating/review-count/velocity comparisons, the
     * trend-vs-trend assessment (both sides computed by the same server-side
     * algorithm), and the real month-by-month history behind the history
     * chart. Nothing here is computed on the client.
     */
    getCompetitorComparison: build.query<
      CompetitorComparisonResult,
      {
        orgId: string
        review_settings_id: string
        google_place_id: string
        competitor: {
          rating?: number | null
          userRatingCount?: number | null
          types?: string[]
          primaryType?: string | null
          priceLevel?: string
          location?: { latitude?: number | null; longitude?: number | null }
        }
      }
    >({
      query: ({ orgId, review_settings_id, google_place_id, competitor }) => ({
        method: 'POST',
        url: `/${orgId}/analysis/competitor-comparison`,
        params: { review_settings_id, google_place_id },
        data: { competitor },
      }),
    }),

    /**
     * Venue-level "how do we stack up nearby" badge for the top of the
     * competitors list view - ranks the venue against the same nearby
     * competitor search the map above already runs. All ranking happens
     * server-side; the client only formats/prettifies the result.
     */
    getLocalStanding: build.query<
      LocalStandingResult,
      { orgId: string; review_settings_id: string }
    >({
      query: ({ orgId, review_settings_id }) => ({
        method: 'GET',
        url: `/${orgId}/analysis/local-standing`,
        params: { review_settings_id },
      }),
    }),

    /**
     * Venue-level summary cards (winning scoreboard / superpower / easiest
     * win) read off the *tracked* competitor list. Degrades to all-null/zero
     * wherever there's no usable tracked data - the frontend simply doesn't
     * render those cards then, with no environment check needed either side.
     */
    getTrackedCompetitorsSummary: build.query<
      TrackedCompetitorsSummaryResult,
      { orgId: string; review_settings_id: string }
    >({
      query: ({ orgId, review_settings_id }) => ({
        method: 'GET',
        url: `/${orgId}/analysis/tracked-competitors-summary`,
        params: { review_settings_id },
      }),
    }),

    /**
     * One review by id, for hydrating a report quote.
     *
     * The reporting warehouse holds no review text by design, so the report
     * returns review IDS and the words are fetched here, from the service that
     * owns them. Two calls per venue page (the best and the worst), cached by
     * RTK so a revisit costs nothing.
     */
    getReviewById: build.query<
      { id: string; review: string | null; rating: number | null; platform: string; created_at: string },
      { orgId: string; reviewId: string }
    >({
      query: ({ orgId, reviewId }) => ({
        method: 'GET',
        url: `/${orgId}/reviews/${reviewId}`,
      }),
    }),

    getOrgReviewPages: build.query<ReviewPageType[], { orgId: string }>({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/${orgId}/reviews/pages`, // branding_settings, template_id, deleted_at missing
      }),
      providesTags: (items = []) => [
        ...items.map((item: any) => ({
          id: item?.id,
          type: 'ReviewPage' as const,
        })),
        { type: 'ReviewPage', id: 'LIST' },
      ],
    }),

    /**
     * Slim counterpart to `getOrgReviewPages` — id, serial, title and active flag
     * only. Prefer this anywhere the list is just being turned into options or
     * links; the fat endpoint pulls body copy, branding and metrics with it.
     */
    getOrgReviewPagesList: build.query<
      ReviewPageListItemType[],
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/${orgId}/reviews/pages/list`,
      }),
      providesTags: (items = []) => [
        ...items.map((item) => ({
          id: item.id,
          type: 'ReviewPage' as const,
        })),
        { type: 'ReviewPage', id: 'LIST' },
      ],
    }),

    deleteReviewPage: build.mutation<
      ReviewPageType,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'DELETE',
        url: `/${orgId}/reviews/pages/${id}`,
      }),
      transformResponse(item: ReviewPageType) {
        toast.success(`Review page deleted`)
        return item
      },
      invalidatesTags: (item: any) => [{ type: 'ReviewPage', id: item?.id }],
    }),

    getReviewsPage: build.query<ReviewPageType, { orgId: string; id: string }>({
      query: ({ orgId, id }) => ({
        method: 'GET',
        url: `/${orgId}/reviews/pages/${id}`,
      }),
      providesTags: (item: any) => [{ type: 'ReviewPage', id: item?.id }],
    }),

    getVenueReviewsPage: build.query<
      ReviewPageType,
      { org_id: string; serial: string }
    >({
      query: ({ org_id, serial }) => ({
        method: 'GET',
        url: `/${org_id}/reviews/pages/venue/${serial}`,
      }),
      providesTags: (item: any) => [{ type: 'ReviewPage', id: item?.id }],
    }),

    updateReviewsPage: build.mutation<
      ReviewPageType,
      { orgId: string; page: ReviewPageType }
    >({
      query: ({ orgId, page }) => ({
        method: 'PUT',
        url: `/${orgId}/reviews/pages/${page.id}`,
        data: page,
      }),
      invalidatesTags: (item: any) => [{ type: 'ReviewPage', id: item?.id }],
      transformResponse(item: ReviewPageType) {
        toast.success('Review page updated')
        return item
      },
    }),

    createReviewsPage: build.mutation<
      ReviewPageType,
      { orgId: string; page: ReviewPageType }
    >({
      query: ({ orgId, page }) => ({
        method: 'POST',
        url: `/${orgId}/reviews/pages`,
        data: { ...page, org_id: orgId },
      }),
      invalidatesTags: (item: any) => [
        { type: 'ReviewPage', id: item?.id },
        { type: 'ReviewPage', id: 'LIST' },
      ],
      transformResponse(item: ReviewPageType) {
        toast.success('Review page created')
        return item
      },
    }),

    updateReviewsPageBrand: build.mutation<
      unknown,
      { orgId: string; id: string; branding_id: string; silent?: boolean }
    >({
      query: ({ orgId, id, branding_id }) => ({
        method: 'PUT',
        url: `/${orgId}/reviews/pages/${id}/brand`,
        data: {
          branding_id,
        },
      }),
      invalidatesTags: (_1, _2, { id }) => [{ type: 'ReviewPage', id }],
      transformResponse(data, _meta, arg) {
        // `silent` is set when we auto-reconcile a stale/deleted branding_id on
        // page load — the user didn't trigger it, so skip the success toast.
        if (!arg.silent) {
          toast.success('Brand kit updated')
        }
        return data
      },
    }),

    getReviewResponses: build.query<
      ServiceCursorPaginatedResponseType<ReviewResponseType> & {
        redirectConversionContext:
          | {
              sentEmails: number
              redirectCounts: {
                total: number
                external: number
              }
              externalReviews: {
                total: number
                confirmed: number
              }
            }
          | {}
      },
      {
        orgId: string
        query: {
          review?: string
          review_page?: string
          serial?: string
          review_rating?: number
          review_platforms?: UserReviewPlatforms[]
          review_sentiment?: ReviewSentimentType
          redirect_conversion?: string
          done?: boolean
          sort_by?: 'asc' | 'desc'
          limit: number
          cursor: string
        }
      }
    >({
      query: ({ orgId, query }) => ({
        method: 'GET',
        url: buildUrl(`/${orgId}/reviews`, query),
      }),
      providesTags: (items: any) => [
        ...(items?.data ?? []).map((item: any) => ({
          id: item?.id,
          type: 'ReviewResponses' as const,
        })),
        { id: 'LIST', type: 'ReviewResponses' },
      ],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        console.log({ queryArgs, endpointName })
        return JSON.stringify({
          orgId: queryArgs.orgId,
          params: {
            ...queryArgs.query,
            cursor: '',
          },
        })
      },
      merge: (currentCache, newItems) => {
        currentCache.links = newItems.links
        currentCache.meta = newItems.meta

        newItems.data.forEach((item: any) => {
          const current = currentCache.data.findIndex(
            (it: any) => it?.id === item?.id
          )
          if (current >= 0) {
            currentCache.data[current] = item
          } else {
            currentCache.data.push(item)
          }
        })
      },
      forceRefetch({ currentArg, previousArg }) {
        console.log(
          { currentArg, previousArg },
          'queryArgs',
          isEqual(currentArg?.query, previousArg?.query)
        )
        return !isEqual(currentArg?.query, previousArg?.query)
        //  return currentArg !== previousArg
      },
    }),

    getReviewResponse: build.query<
      ReviewResponseType,
      {
        orgId: string
        id: string
      }
    >({
      query: ({ orgId, id }) => ({
        method: 'GET',
        url: `/${orgId}/reviews/${id}`,
      }),
      providesTags: (item: any) => [{ id: item?.id, type: 'ReviewResponses' }],
    }),

    updateReview: build.mutation<
      ReviewResponseType,
      {
        orgId: string
        id: string
        review: ReviewResponseType
      }
    >({
      query: ({ orgId, review, id }) => ({
        method: 'PUT',
        url: `/${orgId}/reviews/${id}`,
        data: { ...review },
      }),

      // Optimistically patch every cached responses list that holds this review
      // so a done/undone toggle repaints the row instantly — the infinite-scroll
      // cache merge alone won't repaint a row that isn't on the just-fetched page.
      async onQueryStarted(
        { id, review },
        { dispatch, queryFulfilled, getState }
      ) {
        const entries = tankApi.util.selectInvalidatedBy(getState(), [
          { type: 'ReviewResponses', id },
        ])
        const patches = entries
          .filter((entry) => entry.endpointName === 'getReviewResponses')
          .map((entry) =>
            dispatch(
              tankApi.util.updateQueryData(
                'getReviewResponses',
                entry.originalArgs as never,
                (draft) => {
                  const target = draft.data.find(
                    (item: ReviewResponseType) => item?.id === id
                  )
                  if (target) target.done_at = review.done_at
                }
              )
            )
          )
        try {
          await queryFulfilled
        } catch {
          patches.forEach((patch) => {
            patch.undo()
          })
        }
      },

      invalidatesTags: (item: any) => [
        { id: item?.id, type: 'ReviewResponses' },
      ],
    }),

    getCustomerReviews: build.query<
      ServiceCursorPaginatedResponseType<ReviewResponseType>,
      {
        orgId: string
        orgRegId: string
        query: {
          limit: number
          cursor: string
          excludedTypes?: string[]
          includedTypes?: string[]
          lat?: number
          lng?: number
          maxResults?: number
          override?: boolean
        }
      }
    >({
      query: ({ orgId, orgRegId, query }) => ({
        method: 'GET',
        url: buildUrl(`/${orgId}/reviews/${orgRegId}/reviews`, query),
      }),
    }),

    getPrompts: build.query<CustomPromptType[], { orgId: string }>({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/${orgId}/reviews/prompts`,
      }),
      providesTags: (items = []) => [
        ...items.map((item: any) => ({
          id: item?.id,
          type: 'Prompts' as const,
        })),
        { type: 'Prompts', id: 'LIST' },
      ],
    }),

    getPrompt: build.query<CustomPromptType, { orgId: string; id: string }>({
      query: ({ orgId, id }) => ({
        method: 'GET',
        url: `/${orgId}/reviews/prompts/${id}`,
      }),
      providesTags: (item: any) => [{ type: 'Prompts', id: item?.id }],
    }),

    createPrompt: build.mutation<
      CustomPromptType,
      {
        orgId: string
        data: {
          name: string
          prompt: string
          is_org_default?: boolean
        }
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/${orgId}/reviews/prompts`,
        data,
      }),
      invalidatesTags: [{ type: 'Prompts', id: 'LIST' }],
    }),

    updatePrompt: build.mutation<
      CustomPromptType,
      {
        orgId: string
        id: string
        data: {
          name?: string
          prompt?: string
          is_org_default?: boolean
        }
      }
    >({
      query: ({ orgId, id, data }) => ({
        method: 'PUT',
        url: `/${orgId}/reviews/prompts/${id}`,
        data,
      }),
      invalidatesTags: (item: any) => [
        { type: 'Prompts', id: item?.id },
        { type: 'Prompts', id: 'LIST' },
      ],
    }),

    deletePrompt: build.mutation<
      CustomPromptType,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'DELETE',
        url: `/${orgId}/reviews/prompts/${id}`,
      }),
      invalidatesTags: (item: any) => [
        { type: 'Prompts', id: item?.id },
        { type: 'Prompts', id: 'LIST' },
      ],
    }),

    getCustomCopySets: build.query<CustomCopyType[], { orgId: string }>({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/${orgId}/reviews/custom-copy`,
      }),
      providesTags: (result: any) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'CustomCopy' as const, id })),
              { type: 'CustomCopy', id: 'LIST' },
            ]
          : [{ type: 'CustomCopy', id: 'LIST' }],
    }),

    getCustomCopySet: build.query<
      CustomCopyType,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'GET',
        url: `/${orgId}/reviews/custom-copy/${id}`,
      }),
      providesTags: (result: any) => [{ type: 'CustomCopy', id: result?.id }],
    }),

    createCustomCopySet: build.mutation<
      CustomCopyType,
      {
        orgId: string
        data: {
          name: string
          feedback_placeholder: string
          rating_1: string
          rating_2: string
          rating_3: string
          rating_4: string
          rating_5: string
        }
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/${orgId}/reviews/custom-copy`,
        data,
      }),
      invalidatesTags: [{ type: 'CustomCopy', id: 'LIST' }],
    }),

    updateCustomCopySet: build.mutation<
      CustomCopyType,
      {
        orgId: string
        id: string
        data: {
          name?: string
          feedback_placeholder?: string
          rating_1?: string
          rating_2?: string
          rating_3?: string
          rating_4?: string
          rating_5?: string
        }
      }
    >({
      query: ({ orgId, id, data }) => ({
        method: 'PUT',
        url: `/${orgId}/reviews/custom-copy/${id}`,
        data,
      }),
      invalidatesTags: (result: any) => [
        { type: 'CustomCopy', id: result?.id },
        { type: 'CustomCopy', id: 'LIST' },
      ],
    }),

    deleteCustomCopySet: build.mutation<void, { orgId: string; id: string }>({
      query: ({ orgId, id }) => ({
        method: 'DELETE',
        url: `/${orgId}/reviews/custom-copy/${id}`,
      }),
      invalidatesTags: [{ type: 'CustomCopy', id: 'LIST' }],
    }),

    changePageCustomCopySet: build.mutation<
      ReviewPageType,
      {
        orgId: string
        pageId: string
        data: {
          custom_copy_id: string
        }
      }
    >({
      query: ({ orgId, pageId, data }) => ({
        method: 'PUT',
        url: `/${orgId}/reviews/pages/${pageId}/custom-copy`,
        data,
      }),
      invalidatesTags: (result: any) => [
        { type: 'ReviewPage', id: result?.id },
        { type: 'ReviewPage', id: 'LIST' },
        { type: 'CustomCopy', id: 'LIST' },
      ],
    }),

    refreshPageFrontend: build.mutation<
      ReviewPageType,
      { orgId: string; pageId: string }
    >({
      query: ({ orgId, pageId }) => ({
        method: 'PUT',
        url: `/${orgId}/reviews/pages/${pageId}/refresh`,
      }),
      invalidatesTags: (result: any) => [
        { type: 'ReviewPage', id: result?.id },
        { type: 'ReviewPage', id: 'LIST' },
      ],
    }),

    sendPreview: build.mutation<
      { message: string },
      {
        orgId: string
        id: string
        data: {
          email: string
          name: string
        }
      }
    >({
      query: ({ orgId, id, data }) => ({
        method: 'PUT',
        url: `/${orgId}/reviews/${id}/send/preview`,
        data,
      }),
      transformResponse(
        { message: resMessage }: { message: string },
        _1,
        { data }
      ) {
        toast.success(`Preview sent to ${data.email}`)
        return { message: resMessage }
      },
    }),

    downloadResponses: build.query<
      unknown,
      {
        orgId: string
        query: {
          review_text?: string
          review_page?: string
          serial?: string
          review_rating?: number
          review_platforms?: UserReviewPlatforms
          review_sentiment?: ReviewSentimentType
          done?: boolean
          sort_by?: 'asc' | 'desc'
          limit: number
          cursor: string
        }
      }
    >({
      query: ({ orgId, query }) => ({
        method: 'GET',
        url: buildUrl(`/${orgId}/reviews/reports/download`, query),
      }),
      transformResponse(data: string, _1, { orgId }) {
        const url = window.URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `stampede_${orgId}_review_data.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
      },
    }),

    getSimpleReports: build.query<
      SimpleReviewReportType,
      {
        orgId: string
        query: {
          review?: string
          review_page?: string
          serial?: string
          review_rating?: number
          review_platforms?: UserReviewPlatforms
          review_sentiment?: ReviewSentimentType
          done?: boolean
          start: Date
          end: Date
          access?: string
        }
      }
    >({
      query: ({ orgId, query }) => ({
        method: 'GET',
        url: buildUrl(`/${orgId}/reviews/reports/simple`, {
          ...query,
          start: startOfDay(query.start),
          end: endOfDay(query.end),
        }),
      }),
    }),

    getReports: build.query<
      ReviewReportType,
      {
        orgId: string
        query: {
          review?: string
          review_page?: string
          serial?: string
          review_rating?: number
          review_platforms?: UserReviewPlatforms
          review_sentiment?: ReviewSentimentType
          done?: boolean
          sort?: 'asc' | 'desc'
          groupBy?: 'month' | 'week' | 'day' | 'hour'
          limit?: number
          offset?: number
          start: Date
          end: Date
        }
      }
    >({
      query: ({ orgId, query }) => ({
        method: 'GET',
        url: buildUrl(`/${orgId}/reviews/reports/grouped`, {
          ...query,
          start: startOfDay(query.start),
          end: endOfDay(query.end),
        }),
      }),
    }),

    getEmailReports: build.query<
      {
        sentEmails: number
        totals: {
          sentEmails: number
          totalReviews: number
          responseRate: number
          clickRate: number
          clickToReviewRate: number
          redirects: number
          externalRedirects: number
          externalRedirectsPercentage: number
          externalCompleted: number
          externalCompletedPercentage: number
          viaStampedeExternalCompleted: number
          viaStampedeExternalCompletedPercentage: number
          stampedeReviews: number
          stampedeReviewPercentage: number
        }
        redirects: { count: number; rating: number; percentage: number }[]
        chart: []
      },
      {
        orgId: string
        query: {
          serial?: string
          limit?: number
          offset?: number
          review_settings_ids: string[]
          start: Date
          end: Date
        }
      }
    >({
      query: ({ orgId, query }) => ({
        method: 'GET',
        url: buildUrl(`/${orgId}/reviews/reports/emails`, {
          ...query,
          start: startOfDay(query.start),
          end: endOfDay(query.end),
        }),
      }),
    }),

    sendFeatureRequest: build.mutation<
      unknown,
      {
        org_id: string
        data: {
          name: string
          comment: string
          user: {
            email: string
            first: string
            last: string
            uid: string
            company: string
          }
        }
      }
    >({
      query: ({ org_id, data }) => ({
        method: 'POST',
        url: `/${org_id}/reviews/feature-comments`,
        data,
      }),
    }),

    trackCompetitor: build.mutation<
      {
        success: boolean
        data: {
          competitor_tracker: unknown
          competitor_venue: unknown
        }
      },
      {
        orgId: string
        google_place_id: string
        review_settings_id: string
      }
    >({
      query: ({ orgId, google_place_id, review_settings_id }) => ({
        method: 'POST',
        url: `/${orgId}/competitors/track-competitor`,
        data: {
          google_place_id,
          review_settings_id,
        },
      }),
      invalidatesTags: (_result, _error, { review_settings_id }) => [
        { type: 'TrackedCompetitors', id: review_settings_id },
      ],
    }),

    untrackCompetitor: build.mutation<
      { success: boolean; message: string },
      {
        orgId: string
        google_place_id: string
        review_settings_id: string
      }
    >({
      query: ({ orgId, google_place_id, review_settings_id }) => ({
        method: 'PUT',
        url: `/${orgId}/competitors/untrack-competitor`,
        data: {
          google_place_id,
          review_settings_id,
        },
      }),
      invalidatesTags: (_result, _error, { review_settings_id }) => [
        { type: 'TrackedCompetitors', id: review_settings_id },
      ],
    }),

    getTrackedCompetitors: build.query<
      {
        success: boolean
        data: {
          id: string
          org_id: string
          review_settings_id: string
          google_place_id: string
          is_active: boolean
          created_at: string
          competitor_venue: unknown
        }[]
      },
      {
        orgId: string
        review_settings_id: string
      }
    >({
      query: ({ orgId, review_settings_id }) => ({
        method: 'GET',
        url: `/${orgId}/competitors/tracked-competitors/${review_settings_id}`,
      }),
      providesTags: (_result, _error, { review_settings_id }) => [
        { type: 'TrackedCompetitors', id: review_settings_id },
      ],
    }),

    getCustomLinks: build.query<
      CustomLinkType[],
      {
        orgId: string
        review_settings_id: string
      }
    >({
      query: ({ orgId, review_settings_id }) => ({
        method: 'GET',
        url: `/${orgId}/custom-links/${review_settings_id}`,
      }),
      providesTags: (_result, _error, { review_settings_id }) => [
        { type: 'CustomLinks', id: review_settings_id },
      ],
    }),

    createCustomLink: build.mutation<
      CustomLinkType,
      {
        orgId: string
        data: {
          review_settings_id: string
          url: string
          label: string
          is_active?: boolean
        }
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/${orgId}/custom-links`,
        data,
      }),
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'CustomLinks', id: data.review_settings_id },
      ],
    }),

    updateCustomLink: build.mutation<
      CustomLinkType,
      {
        orgId: string
        id: string
        data: {
          url?: string
          label?: string
          is_active?: boolean
        }
      }
    >({
      query: ({ orgId, id, data }) => ({
        method: 'PUT',
        url: `/${orgId}/custom-links/${id}`,
        data,
      }),
      invalidatesTags: (result: any) => [
        { type: 'CustomLinks', id: result?.review_settings_id },
      ],
    }),

    deleteCustomLink: build.mutation<
      { success: boolean; message: string },
      {
        orgId: string
        id: string
        review_settings_id: string
      }
    >({
      query: ({ orgId, id }) => ({
        method: 'DELETE',
        url: `/${orgId}/custom-links/${id}`,
      }),
      invalidatesTags: (_result, _error, { review_settings_id }) => [
        { type: 'CustomLinks', id: review_settings_id },
      ],
    }),

    getGenerationRules: build.query<
      AutoGenerationRuleType[],
      { orgId: string; review_settings_id: string }
    >({
      query: ({ orgId, review_settings_id }) => ({
        method: 'GET',
        url: `/${orgId}/generation/${review_settings_id}`,
      }),
      providesTags: (_result, _error, { review_settings_id }) => [
        { type: 'GenerationRules', id: review_settings_id },
      ],
    }),

    getGenerationRule: build.query<
      AutoGenerationRuleType,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'GET',
        url: `/${orgId}/generation/rule/${id}`,
      }),
      providesTags: (result: any) => [
        { type: 'GenerationRules', id: result?.id },
      ],
    }),

    createGenerationRule: build.mutation<
      AutoGenerationRuleType,
      {
        orgId: string
        data: {
          review_settings_id: string
          name: string
          enabled?: boolean
          min_rating?: number
          max_rating?: number
          sentiment?: 'positive' | 'negative' | 'neutral' | 'mixed'
          platforms?: ('google' | 'facebook' | 'tripadvisor' | 'stampede')[]
          ai_prompt_id?: string
          response_delay?: number
          auto_approve?: boolean
        }
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/${orgId}/generation`,
        data,
      }),
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'GenerationRules', id: data.review_settings_id },
      ],
    }),

    updateGenerationRule: build.mutation<
      AutoGenerationRuleType,
      {
        orgId: string
        id: string
        data: {
          name?: string
          enabled?: boolean
          min_rating?: number | null
          max_rating?: number | null
          sentiment?: 'positive' | 'negative' | 'neutral' | 'mixed' | null
          platforms?:
            | ('google' | 'facebook' | 'tripadvisor' | 'stampede')[]
            | null
          ai_prompt_id?: string | null | undefined
          response_delay?: number
          auto_approve?: boolean
        }
      }
    >({
      query: ({ orgId, id, data }) => ({
        method: 'PUT',
        url: `/${orgId}/generation/${id}`,
        data,
      }),
      invalidatesTags: (result: any) => [
        { type: 'GenerationRules', id: result?.id },
        { type: 'GenerationRules', id: result?.review_settings_id },
      ],
    }),

    deleteGenerationRule: build.mutation<
      unknown,
      { orgId: string; id: string; review_settings_id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'DELETE',
        url: `/${orgId}/generation/${id}`,
      }),
      invalidatesTags: (_result, _error, { review_settings_id }) => [
        { type: 'GenerationRules', id: review_settings_id },
      ],
    }),

    getConfirmationExemptions: build.query<
      {
        exemptions: {
          id: string
          created_at: string
          updated_at: string
          platform: 'google' | 'stampede'
          org_id: string
          user_id: string
        }[]
        count: number
      },
      { orgId: string; uid: string }
    >({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/${orgId}/generation/confirmation-exemptions`,
      }),
      providesTags: (result: any, _error, { uid }) => [
        { type: 'ConfirmationExemptions', id: uid },
      ],
    }),

    updateGenerationRuleConfirmationExemptions: build.mutation<
      {
        updatedRule: AutoGenerationRuleType
        message: string
        user: {
          first?: string
          last?: string
          email?: string
          uid: string
          reply_type: string
        }
      },
      {
        orgId: string
        data: {
          first?: string
          last?: string
          email?: string
          uid: string
          reply_type: string
        }
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'PUT',
        url: `/${orgId}/generation/confirmation-exemptions`,
        data,
      }),
      invalidatesTags: (_result: any, _error, { data }) => [
        { type: 'ConfirmationExemptions', id: data.uid },
      ],
    }),

    // Org-wide generation rules endpoints
    getOrgGenerationRules: build.query<
      AutoGenerationRuleType[],
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/${orgId}/generation/org`,
      }),
      providesTags: (_result: any, _error, { orgId }) => [
        { type: 'GenerationRules', id: orgId },
      ],
    }),

    createOrgGenerationRule: build.mutation<
      AutoGenerationRuleType,
      {
        orgId: string
        data: {
          name: string
          enabled?: boolean
          min_rating?: number
          max_rating?: number
          sentiment?: string
          platforms?: ('google' | 'facebook' | 'tripadvisor' | 'stampede')[]
          serials?: string[]
          ai_prompt_id?: string
          response_delay?: number
          auto_approve?: boolean
        }
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/${orgId}/generation/org`,
        data,
      }),
      invalidatesTags: (_result: any, _error, { orgId }) => [
        { type: 'GenerationRules', id: orgId },
      ],
    }),

    updateOrgGenerationRule: build.mutation<
      AutoGenerationRuleType,
      {
        orgId: string
        id: string
        data: {
          name?: string
          enabled?: boolean
          min_rating?: number | null
          max_rating?: number | null
          sentiment?: string | null
          platforms?:
            | ('google' | 'facebook' | 'tripadvisor' | 'stampede')[]
            | null
          serials?: string[] | null
          ai_prompt_id?: string | null
          response_delay?: number
          auto_approve?: boolean
        }
      }
    >({
      query: ({ orgId, id, data }) => ({
        method: 'PUT',
        url: `/${orgId}/generation/org/${id}`,
        data,
      }),
      invalidatesTags: (_result: any, _error, { orgId }) => [
        { type: 'GenerationRules', id: orgId },
      ],
    }),

    deleteOrgGenerationRule: build.mutation<
      unknown,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'DELETE',
        url: `/${orgId}/generation/org/${id}`,
      }),
      invalidatesTags: (_result: any, _error, { orgId }) => [
        { type: 'GenerationRules', id: orgId },
      ],
    }),

    // Org-wide pending replies query
    getOrgPendingReplies: build.query<
      PendingRepliesListResponse,
      {
        orgId: string
        query?: {
          status?: 'PENDING' | 'APPROVED' | 'REJECTED'
          limit?: number
          offset?: number
        }
      }
    >({
      query: ({ orgId, query = {} }) => ({
        method: 'GET',
        url: `/${orgId}/pending-replies/org`,
        params: query,
      }),
      providesTags: (result: any, _error, { orgId }) => [
        ...(result?.data ?? []).map((item: any) => ({
          id: item?.id,
          type: 'PendingReplies' as const,
        })),
        { id: 'LIST', type: 'PendingReplies' },
        { id: orgId, type: 'PendingReplies' },
      ],
    }),

    getPendingReplies: build.query<
      PendingRepliesListResponse,
      {
        orgId: string
        query?: {
          status?: 'PENDING' | 'APPROVED' | 'REJECTED'
          review_settings_id?: string
          limit?: number
          offset?: number
        }
      }
    >({
      query: ({ orgId, query = {} }) => ({
        method: 'GET',
        url: `/${orgId}/pending-replies`,
        params: query,
      }),
      providesTags: (result: any) => [
        ...(result?.data ?? []).map((item: any) => ({
          id: item?.id,
          type: 'PendingReplies' as const,
        })),
        { id: 'LIST', type: 'PendingReplies' },
      ],
    }),

    getPendingReply: build.query<
      PendingReplyType,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'GET',
        url: `/${orgId}/pending-replies/${id}`,
      }),
      providesTags: (result: any) => [
        { type: 'PendingReplies', id: result?.id },
      ],
    }),

    acceptPendingReply: build.mutation<
      PendingReplyType,
      { orgId: string; id: string; approved_by?: string }
    >({
      query: ({ orgId, id, approved_by }) => ({
        method: 'POST',
        url: `/${orgId}/pending-replies/${id}/accept`,
        data: { approved_by },
      }),
      invalidatesTags: (result: any) => [
        { type: 'PendingReplies', id: result?.id },
        { id: 'LIST', type: 'PendingReplies' },
        { type: 'ReviewResponses', id: result?.review_id },
      ],
    }),

    batchAcceptPendingReplies: build.mutation<
      BatchAcceptResponse,
      { orgId: string; pending_reply_ids: string[]; approved_by?: string }
    >({
      query: ({ orgId, pending_reply_ids, approved_by }) => ({
        method: 'POST',
        url: `/${orgId}/pending-replies/batch-accept`,
        data: { pending_reply_ids, approved_by },
      }),
      invalidatesTags: (_result, _error, { pending_reply_ids }) => [
        ...pending_reply_ids.map((id) => ({
          type: 'PendingReplies' as const,
          id,
        })),
        { id: 'LIST', type: 'PendingReplies' },
        { id: 'LIST', type: 'ReviewResponses' },
      ],
    }),

    rejectPendingReply: build.mutation<
      PendingReplyType,
      { orgId: string; id: string; approved_by?: string }
    >({
      query: ({ orgId, id, approved_by }) => ({
        method: 'POST',
        url: `/${orgId}/pending-replies/${id}/reject`,
        data: { approved_by },
      }),
      invalidatesTags: (result: any) => [
        { type: 'PendingReplies', id: result?.id },
        { id: 'LIST', type: 'PendingReplies' },
        { type: 'ReviewResponses', id: result?.review_id },
      ],
    }),

    editPendingReply: build.mutation<
      PendingReplyType,
      { orgId: string; id: string; generated_reply: string }
    >({
      query: ({ orgId, id, generated_reply }) => ({
        method: 'PUT',
        url: `/${orgId}/pending-replies/${id}`,
        data: { generated_reply },
      }),
      invalidatesTags: (result: any) => [
        { type: 'PendingReplies', id: result?.id },
        { id: 'LIST', type: 'PendingReplies' },
        { type: 'ReviewResponses', id: result?.review_id },
      ],
    }),

    regeneratePendingReply: build.mutation<
      PendingReplyType & { adjustments_applied: string[] },
      { orgId: string; id: string; adjustments: string[] }
    >({
      query: ({ orgId, id, adjustments }) => ({
        method: 'POST',
        url: `/${orgId}/pending-replies/${id}/regenerate`,
        data: { adjustments },
      }),
      invalidatesTags: (result: any) => [
        { type: 'PendingReplies', id: result?.id },
        { id: 'LIST', type: 'PendingReplies' },
        { type: 'ReviewResponses', id: result?.review_id },
      ],
    }),

    batchRegeneratePendingReplies: build.mutation<
      {
        summary: {
          total: number
          success: number
          failed: number
        }
        results: {
          id: string
          status: 'success' | 'error'
          generated_reply?: string
          adjustments_applied?: string[]
          message?: string
        }[]
        errors?: {
          id: string
          error: string
        }[]
      },
      {
        orgId: string
        regenerations: {
          pending_reply_id: string
          adjustments: string[]
        }[]
      }
    >({
      query: ({ orgId, regenerations }) => ({
        method: 'POST',
        url: `/${orgId}/pending-replies/batch-regenerate`,
        data: { regenerations },
      }),
      invalidatesTags: (_result, _error, { regenerations }) => [
        ...regenerations.map(({ pending_reply_id }) => ({
          type: 'PendingReplies' as const,
          id: pending_reply_id,
        })),
        { id: 'LIST', type: 'PendingReplies' },
        { id: 'LIST', type: 'ReviewResponses' },
      ],
    }),

    getPendingReplyActivity: build.query<
      PendingReplyActivityResponse,
      { orgId: string; id: string }
    >({
      query: ({ orgId, id }) => ({
        method: 'GET',
        url: `/${orgId}/pending-replies/${id}/activity`,
      }),
      providesTags: (result: any) => [
        { type: 'PendingReplies', id: result?.pending_reply_id },
      ],
    }),

    getActivityLog: build.query<
      ActivityLogResponse,
      { orgId: string; query?: ActivityLogQueryParams }
    >({
      query: ({ orgId, query = {} }) => ({
        method: 'GET',
        url: `/${orgId}/ai-analytics/activity-log`,
        params: {
          ...(query.limit && { limit: query.limit }),
          ...(query.cursor && { cursor: query.cursor }),
          ...(query.sort_by && { sort_by: query.sort_by }),
          ...(query.serial && { serial: query.serial }),
          ...(query.event_type && { event_type: query.event_type }),
          ...(query.actor_type && { actor_type: query.actor_type }),
          ...(query.auto_gen_rule_id && {
            auto_gen_rule_id: query.auto_gen_rule_id,
          }),
          ...(query.new_status && { new_status: query.new_status }),
          ...(query.start_date && { start_date: query.start_date }),
          ...(query.end_date && { end_date: query.end_date }),
          ...(query.show_count && { show_count: query.show_count }),
        },
      }),
      providesTags: (_result, _error, { orgId }) => [
        { type: 'PendingReplies', id: `ACTIVITY-LOG-${orgId}` },
      ],
    }),

    getAiAnalyticsOverview: build.query<
      {
        total_responses_generated: number
        total_responses_approved: number
        total_responses_rejected: number
        total_manually_generated: number
        total_manually_approved: number
        overall_approval_rate: number
        active_rules_count: number
        total_rules_count: number
      },
      { orgId: string; serial?: string }
    >({
      query: ({ orgId, serial }) => ({
        method: 'GET',
        url: `/${orgId}/ai-analytics/overview`,
        params: serial ? { serial } : undefined,
      }),
    }),

    getAiAnalyticsByRule: build.query<
      {
        metrics_by_rule: {
          rule_id: string
          rule_name: string
          enabled: boolean
          responses_generated: number
          responses_approved: number
          responses_rejected: number
          approval_rate: number
        }[]
      },
      { orgId: string; serial?: string }
    >({
      query: ({ orgId, serial }) => ({
        method: 'GET',
        url: `/${orgId}/ai-analytics/by-rule`,
        params: serial ? { serial } : undefined,
      }),
    }),

    getAiAnalyticsByPrompt: build.query<
      {
        metrics_by_prompt: {
          prompt_id: string
          prompt_name: string
          responses_generated: number
          responses_approved: number
          responses_rejected: number
          manually_generated: number
          manually_approved: number
          approval_rate: number
        }[]
      },
      { orgId: string; serial?: string }
    >({
      query: ({ orgId, serial }) => ({
        method: 'GET',
        url: `/${orgId}/ai-analytics/by-prompt`,
        params: serial ? { serial } : undefined,
      }),
    }),

    // Queue Status
    getAiAnalyticsQueueStatus: build.query<
      AiAnalyticsQueueStatusResponse,
      { orgId: string; serial?: string }
    >({
      query: ({ orgId, serial }) => ({
        method: 'GET',
        url: `/${orgId}/ai-analytics/queue-status`,
        params: serial ? { serial } : undefined,
      }),
    }),

    // Response Times
    getAiAnalyticsResponseTimes: build.query<
      AiAnalyticsResponseTimesResponse,
      { orgId: string; serial?: string; start_date?: string; end_date?: string }
    >({
      query: ({ orgId, serial, start_date, end_date }) => ({
        method: 'GET',
        url: `/${orgId}/ai-analytics/response-times`,
        params: {
          ...(serial && { serial }),
          ...(start_date && { start_date }),
          ...(end_date && { end_date }),
        },
      }),
    }),

    // Activity Feed
    getAiAnalyticsActivityFeed: build.query<
      AiAnalyticsActivityFeedResponse,
      { orgId: string; serial?: string; limit?: number; offset?: number }
    >({
      query: ({ orgId, serial, limit = 50, offset = 0 }) => ({
        method: 'GET',
        url: `/${orgId}/ai-analytics/activity-feed`,
        params: {
          limit,
          offset,
          ...(serial && { serial }),
        },
      }),
    }),

    // Period Comparison
    getAiAnalyticsComparison: build.query<
      AiAnalyticsComparisonResponse,
      { orgId: string; period?: '7d' | '30d' | '90d'; serial?: string }
    >({
      query: ({ orgId, period = '7d', serial }) => ({
        method: 'GET',
        url: `/${orgId}/ai-analytics/comparison`,
        params: {
          period,
          ...(serial && { serial }),
        },
      }),
    }),

    // By Rating
    getAiAnalyticsByRating: build.query<
      AiAnalyticsByRatingResponse,
      { orgId: string; serial?: string; start_date?: string; end_date?: string }
    >({
      query: ({ orgId, serial, start_date, end_date }) => ({
        method: 'GET',
        url: `/${orgId}/ai-analytics/by-rating`,
        params: {
          ...(serial && { serial }),
          ...(start_date && { start_date }),
          ...(end_date && { end_date }),
        },
      }),
    }),

    // Trends
    getAiAnalyticsTrends: build.query<
      AiAnalyticsTrendsResponse,
      {
        orgId: string
        serial?: string
        interval?: 'day' | 'week' | 'month'
        period?: '7d' | '14d' | '30d' | '90d'
        start_date?: string
        end_date?: string
      }
    >({
      query: ({
        orgId,
        serial,
        interval = 'day',
        period,
        start_date,
        end_date,
      }) => ({
        method: 'GET',
        url: `/${orgId}/ai-analytics/trends`,
        params: {
          interval,
          ...(serial && { serial }),
          ...(period && { period }),
          ...(start_date && { start_date }),
          ...(end_date && { end_date }),
        },
      }),
    }),

    getReviewMetadata: build.query<
      {
        platforms: Record<string, string>
        sentiments: Record<string, string>
      },
      void
    >({
      query: () => ({
        method: 'GET',
        url: '/public/review-metadata',
      }),
    }),

    // Metrics endpoints
    getOrgMetricsAccumulations: build.query<
      ReviewMetricsAccumulationType[],
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/${orgId}/metrics/accumulation`,
      }),
    }),

    getReviewSettingsMetricsAccumulation: build.query<
      ReviewMetricsAccumulationType,
      { orgId: string; review_settings_id: string }
    >({
      query: ({ orgId, review_settings_id }) => ({
        method: 'GET',
        url: `/${orgId}/metrics/accumulation/${review_settings_id}`,
      }),
    }),

    getSerialMetricsAccumulation: build.query<
      ReviewMetricsAccumulationType,
      { orgId: string; serial: string }
    >({
      query: ({ orgId, serial }) => ({
        method: 'GET',
        url: `/${orgId}/metrics/accumulation/venue/${serial}`,
      }),
    }),

    getMetricsTimeseries: build.query<
      MetricsTimeseriesDataPoint[],
      { orgId: string; period?: 'week' | 'month' | 'year' }
    >({
      query: ({ orgId, period = 'month' }) => ({
        method: 'GET',
        url: `/${orgId}/metrics/timeseries`,
        params: { period },
      }),
    }),
    getVenueMetricsTimeseries: build.query<
      MetricsTimeseriesDataPoint[],
      { orgId: string; serial: string; start?: string; end?: string }
    >({
      query: ({ orgId, serial, start, end }) => ({
        method: 'GET',
        url: `/${orgId}/metrics/timeseries/venue/${serial}`,
        params: { start, end },
      }),
    }),

    // Notification Emails endpoints
    getNotificationEmails: build.query<
      NotificationEmailsListResponse,
      {
        orgId: string
        email_type?: string
        recipient_email?: string
        template_name?: string
        start_date?: string
        end_date?: string
        limit?: number
        offset?: number
      }
    >({
      query: ({
        orgId,
        email_type,
        recipient_email,
        template_name,
        start_date,
        end_date,
        limit,
        offset,
      }) => ({
        method: 'GET',
        url: `/${orgId}/notification-emails`,
        params: {
          ...(email_type && { email_type }),
          ...(recipient_email && { recipient_email }),
          ...(template_name && { template_name }),
          ...(start_date && { start_date }),
          ...(end_date && { end_date }),
          ...(limit && { limit }),
          ...(offset && { offset }),
        },
      }),
      providesTags: (result, _error, { orgId }) => [
        ...(result?.emails ?? []).map((email) => ({
          type: 'NotificationEmails' as const,
          id: email.id,
        })),
        { type: 'NotificationEmails', id: `LIST-${orgId}` },
      ],
    }),

    getNotificationEmailById: build.query<
      NotificationEmailType,
      { orgId: string; email_id: string }
    >({
      query: ({ orgId, email_id }) => ({
        method: 'GET',
        url: `/${orgId}/notification-emails/${email_id}`,
      }),
      providesTags: (_result, _error, { email_id }) => [
        { type: 'NotificationEmails', id: email_id },
      ],
    }),

    searchNotificationEmails: build.query<
      NotificationEmailsSearchResponse,
      {
        orgId: string
        query: string
        search_field?: 'recipient_email' | 'email_subject' | 'template_name'
      }
    >({
      query: ({ orgId, query: searchQuery, search_field }) => ({
        method: 'GET',
        url: `/${orgId}/notification-emails/search`,
        params: {
          query: searchQuery,
          ...(search_field && { search_field }),
        },
      }),
      providesTags: (result, _error, { orgId }) => [
        ...(result?.emails ?? []).map((email) => ({
          type: 'NotificationEmails' as const,
          id: email.id,
        })),
        { type: 'NotificationEmails', id: `SEARCH-${orgId}` },
      ],
    }),

    resendNotificationEmail: build.mutation<
      ResendEmailResponse,
      { orgId: string; email_id: string }
    >({
      query: ({ orgId, email_id }) => ({
        method: 'POST',
        url: `/${orgId}/notification-emails/${email_id}/resend`,
      }),
      invalidatesTags: (_result, _error, { email_id, orgId }) => [
        { type: 'NotificationEmails', id: email_id },
        { type: 'NotificationEmails', id: `LIST-${orgId}` },
      ],
    }),

    sendNotificationEmailPreview: build.mutation<
      PreviewEmailResponse,
      { orgId: string; email_id: string; recipient_email?: string }
    >({
      query: ({ orgId, email_id, recipient_email }) => ({
        method: 'POST',
        url: `/${orgId}/notification-emails/${email_id}/preview`,
        data: {
          ...(recipient_email && { recipient_email }),
        },
      }),
      invalidatesTags: (_result, _error, { email_id, orgId }) => [
        { type: 'NotificationEmails', id: email_id },
        { type: 'NotificationEmails', id: `LIST-${orgId}` },
      ],
    }),

    // Admin endpoints
    getOrganizationTokensOverview: build.query<
      {
        tokens_count: number
        unique_accounts_count: number
        tokens: {
          token_id: string
          org_id?: string
          expires_at: Date
          accounts_count?: number
          accounts: {
            account_id: string
            name: string
            account_name: string
            type: string
            verification_state: string
          }[]
          error?: string
        }[]
        unique_accounts: {
          account_id: string
          name: string
          account_name: string
          type: string
          verification_state: string
          accessible_by_tokens: string[]
        }[]
      },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/${orgId}/google/admin/tokens/overview`,
      }),
    }),

    getOrganizationGoogleData: build.query<
      {
        org_id: string
        accounts_count: number
        active_accounts_count: number
        deleted_accounts_count: number
        locations_count: number
        active_locations_count: number
        deleted_locations_count: number
        accounts: {
          id: string
          account_id: string
          name: string
          account_name: string
          type: string
          role: string
          verification_state: string
          vetted_state: string
          org_id: string
          created_at: Date
          updated_at: Date
          deleted_at: Date | null
          locations_count: number
          locations: {
            id: string
            location_id: string
            account_id: string
            name: string
            title: string
            language_code: string
            store_code: string
            primary_category_name: string
            primary_category_display: string
            latitude: number
            longitude: number
            region_code: string
            postal_code: string
            locality: string
            address_lines: string[]
            place_id: string
            maps_uri: string
            new_review_uri: string
            org_id: string
            review_settings_id: string
            voice_of_merchant?: {
              hasVoiceOfMerchant: boolean
              hasBusinessAuthority: boolean
            } | null
            created_at: Date
            updated_at: Date
            deleted_at: Date | null
          }[]
        }[]
        all_locations: {
          id: string
          location_id: string
          account_id: string
          name: string
          title: string
          language_code: string
          store_code: string
          primary_category_name: string
          primary_category_display: string
          latitude: number
          longitude: number
          region_code: string
          postal_code: string
          locality: string
          address_lines: string[]
          place_id: string
          maps_uri: string
          new_review_uri: string
          org_id: string
          review_settings_id: string
          voice_of_merchant?: {
            hasVoiceOfMerchant: boolean
            hasBusinessAuthority: boolean
          } | null
          created_at: Date
          updated_at: Date
          deleted_at: Date | null
        }[]
      },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/${orgId}/google/admin/data`,
        params: { vom: true },
      }),
    }),

    getGoogleApiLocationsForAccount: build.mutation<
      {
        token_id: string
        account_id: string
        org_id: string
        token_status: {
          expired: boolean
          expires_at: Date
          deleted: boolean
        }
        google_locations_count: number
        database_locations_count: number
        locations_from_google: {
          name: string
          title: string
          languageCode: string
          storeCode: string
          categories: any
          storefrontAddress: any
          latlng: any
          openInfo: any
          phoneNumbers: any
          metadata: any
          in_database: boolean
          deleted_in_database: boolean
          database_deleted_at: Date | null
        }[]
        locations_only_in_database: {
          location_id: string
          name: string
          deleted_at: Date | null
          status: string
        }[]
      },
      {
        orgId: string
        token_id: string
        account_id: string
      }
    >({
      query: ({ orgId, token_id, account_id }) => ({
        method: 'POST',
        url: `/${orgId}/google/admin/accounts/locations`,
        data: { token_id, account_id },
      }),
    }),

    getResponseRate: build.query<ResponseRateResponse, ResponseRateQueryParams>(
      {
        query: ({ orgId, review_settings_id, serial, from, to }) => ({
          url: `/${orgId}/metrics/response-rate`,
          method: 'GET',
          params: {
            ...(review_settings_id ? { review_settings_id } : {}),
            ...(serial ? { serial } : {}),
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
          },
        }),
      }
    ),

    searchGoogleBusiness: build.mutation<
      {
        query: string
        results_count: number
        environment: string
        token_used: string
        google_locations: {
          name: string
          title: string
          storefrontAddress: any
          websiteUri: string
          primaryPhone: string
          categories: any
          database_status: {
            exists_in_database: boolean
            database_location_id: string | null
            database_account_id: string | null
            database_org_id: string | null
            database_deleted: boolean
          }
        }[]
      },
      {
        orgId: string
        query: string
      }
    >({
      query: ({ orgId, query }) => ({
        method: 'POST',
        url: `/${orgId}/google/admin/search`,
        data: { query },
      }),
    }),
  }),
})

export default tankApi
